import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings, ArrowLeft, Save, Mail, Bell, Loader2, Plus, X, Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContratsParametres() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const fetchSettings = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/contracts/settings/resiliation-notifications`, { headers });
      setRecipients(res.data.recipients || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();
    
    if (!email) {
      setEmailError('Veuillez saisir une adresse email');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('Adresse email invalide');
      return;
    }
    
    if (recipients.includes(email)) {
      setEmailError('Cette adresse est déjà dans la liste');
      return;
    }
    
    setRecipients([...recipients, email]);
    setNewEmail('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove) => {
    setRecipients(recipients.filter(e => e !== emailToRemove));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/contracts/settings/resiliation-notifications`,
        { recipients },
        { headers }
      );
      toast.success('Paramètres enregistrés');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSendRecap = async () => {
    if (recipients.length === 0) {
      toast.error("Ajoutez au moins un destinataire avant d'envoyer le récapitulatif");
      return;
    }
    
    setSendingRecap(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API}/contracts/cron/monthly-resiliation-recap`,
        {},
        { headers }
      );
      
      if (res.data.resiliations_count === 0) {
        toast.info('Aucune résiliation prévue ce mois-ci');
      } else {
        toast.success(`Récapitulatif envoyé (${res.data.resiliations_count} résiliation(s))`);
      }
    } catch (error) {
      console.error('Error sending recap:', error);
      toast.error("Erreur lors de l'envoi du récapitulatif");
    } finally {
      setSendingRecap(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contrats-parametres">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Paramètres des contrats
            </h1>
            <p className="text-muted-foreground text-sm">
              Configurez les notifications de résiliation
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>

      {/* Notifications résiliation */}
      <Card className="bg-white border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-orange-500" />
            Notifications de résiliation
          </CardTitle>
          <CardDescription>
            Configurez les destinataires des emails de notification lors de la réception d&apos;une demande de résiliation client et du récapitulatif mensuel.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Liste des destinataires */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Destinataires des notifications
            </Label>
            
            {recipients.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-4">
                {recipients.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="flex items-center gap-1 py-1.5 px-3"
                  >
                    <Mail className="w-3 h-3" />
                    {email}
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-1 hover:text-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <Alert className="mb-4 bg-amber-50 border-amber-200">
                <AlertDescription className="text-amber-800">
                  Aucun destinataire configuré. Les notifications de résiliation ne seront pas envoyées.
                </AlertDescription>
              </Alert>
            )}
            
            {/* Ajouter un email */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="exemple@betaye.fr"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setEmailError('');
                  }}
                  onKeyPress={handleKeyPress}
                  className={emailError ? 'border-red-500' : ''}
                  data-testid="new-email-input"
                />
                {emailError && (
                  <p className="text-red-500 text-xs mt-1">{emailError}</p>
                )}
              </div>
              <Button
                variant="outline"
                onClick={handleAddEmail}
                data-testid="add-email-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter
              </Button>
            </div>
          </div>

          {/* Info sur les notifications */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-sm">Les destinataires recevront :</h4>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">•</span>
                <span><strong>Notification immédiate</strong> lors de la validation d&apos;une demande de résiliation client</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">•</span>
                <span><strong>Récapitulatif mensuel</strong> (1er du mois) listant les contrats se terminant ce mois-ci</span>
              </li>
            </ul>
          </div>

          {/* Envoi manuel du récapitulatif */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">Envoyer le récapitulatif maintenant</h4>
                <p className="text-xs text-muted-foreground">
                  Envoie immédiatement le récapitulatif des résiliations du mois en cours
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleSendRecap}
                disabled={sendingRecap || recipients.length === 0}
                className="gap-2"
                data-testid="send-recap-btn"
              >
                {sendingRecap ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Envoyer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info CRON */}
      <Card className="bg-white border">
        <CardHeader>
          <CardTitle className="text-base">Jobs automatiques</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <Bell className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-blue-900">Récapitulatif mensuel</p>
                <p className="text-blue-700 text-xs">
                  Le 1er de chaque mois à 8h00, un email est envoyé avec la liste des contrats dont la date d&apos;effet de résiliation tombe ce mois.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                <Settings className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-900">Mise à jour automatique des statuts</p>
                <p className="text-green-700 text-xs">
                  Chaque jour, les contrats dont la date d&apos;effet est passée sont automatiquement passés au statut &quot;Résilié&quot;.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
